import { ChangeDetectionStrategy, Component, ContentChildren, DestroyRef, HostBinding, QueryList, effect, inject, signal } from '@angular/core';

import { MenuInteractiveItemComponent } from '../menu-interactive-item/menu-interactive-item.component';
import { MenuLinkItemComponent } from '../menu-link-item/menu-link-item.component';
import { MenuItemComponent } from '../menu-item/menu-item.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ds-menu',
  styles: [':host { display: contents; }'],
  template: `<ul class="ds-menu">
    <ng-content></ng-content>
  </ul>`,
})
export class MenuComponent {

  @HostBinding('attr.role') role = 'list';

  @ContentChildren(MenuInteractiveItemComponent, { descendants: true })
  set interactiveItems(value: QueryList<MenuInteractiveItemComponent>) {
    this.$interactiveItems.set(value.toArray());
  }

  @ContentChildren(MenuLinkItemComponent, { descendants: true })
  set linkItems(value: QueryList<MenuLinkItemComponent>) {
    this.$linkItems.set(value.toArray());
  }

  @ContentChildren(MenuItemComponent, { descendants: true })
  set items(value: QueryList<MenuItemComponent>) {
    this.$items.set(value.toArray());
  }

  $interactiveItems = signal<MenuInteractiveItemComponent[]>([]);
  $linkItems = signal<MenuLinkItemComponent[]>([]);
  $items = signal<MenuItemComponent[]>([]);

  destroyRef = inject(DestroyRef);

  $itemList = signal<(MenuInteractiveItemComponent | MenuLinkItemComponent | MenuItemComponent)[]>([]);

  constructor() {
    effect(() => {
      this.setItems();
      this.createEvents();
    });
  }

  setItems() {
    const items = [
      ...this.$interactiveItems(),
      ...this.$linkItems(),
      ...this.$items(),
    ];

    items.sort((a, b) => {
      const posA = a.elementRef.nativeElement.compareDocumentPosition(b.elementRef.nativeElement);
      return posA & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });

    this.$itemList.set(items);
  }

  createEvents() {
    this.$itemList()
      .filter(item => !(item instanceof MenuItemComponent))
      .forEach(item => {
        const nodeList = item.elementRef.nativeElement.querySelectorAll('.ds-menu-item');
        Array.from(nodeList)[nodeList.length - 1].addEventListener('click', () => this.selectItem(item));
      });
  }

  selectItem(node: MenuInteractiveItemComponent | MenuLinkItemComponent) {
    this.$itemList()
      .filter(item => !(item instanceof MenuItemComponent))
      .forEach(item => item.$_isSelected.set(item === node));
  }

}
